import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FireServices } from "../../../../../shared/services/api/fire/FireServices";
import { showToastHelper } from "../../utils/showToastHelper";
import { mapFormDataToQuotationSavePayload } from "../../utils/quotationPayloadSaveMapper";
import { useToast } from "../../../../../shared/hooks";
import { ClientCompanyData, FireCreateQuotePayload } from "../types/fire";

export interface SaveQuoteParams {
    clientId: string | null;
    companyId: string | null;
    brokerageId: number | null;
    propertyTypeId: string | null;
    totalQuotation: number;
    companyData?: ClientCompanyData;
    isLoadingExternal?: boolean;
    quotationId?: string;
    selectionData?: any;
    activeCoverages: any[];
    transformDateToISO: (date?: string) => string | null;
    quotationResult?: any;
}

export function useFireSave({
    formRef,
    toastHandler,
}: {
    formRef: any;
    toastHandler: ReturnType<typeof useToast>;
}) {
    const [isSaving, setIsSaving] = useState(false);
    const navigate = useNavigate();

    const handleSaveQuotation = useCallback(
        async ({
            clientId,
            companyId,
            brokerageId,
            propertyTypeId,
            totalQuotation,
            companyData,
            isLoadingExternal,
            quotationId,
            selectionData,
            activeCoverages,
            transformDateToISO,
            quotationResult,
        }: SaveQuoteParams): Promise<void> => {
            if (!formRef.current?.getData || isLoadingExternal || !clientId || !companyId || !propertyTypeId || totalQuotation === 0) {
                showToastHelper(toastHandler, "Preencha todos os dados obrigatórios antes de salvar.", "info");
                return;
            }

            setIsSaving(true);

            try {
                const formData = formRef.current.getData();
                const dependencies = {
                    clientId,
                    brokerageId: String(brokerageId),
                    companyId,
                    selectionData,
                    localCoverages: activeCoverages,
                    companyData,
                    totalQuotation,
                    transformDateToISO,
                    quotationResultCoverages: quotationResult?.coverages ?? [],
                };

                const quotePayload: FireCreateQuotePayload = mapFormDataToQuotationSavePayload(formData, dependencies);

                if (quotationId && quotationId !== "novo") {
                    // Edição (PUT)
                    await FireServices.FireUpdateQuotation(quotationId, quotePayload);
                    showToastHelper(toastHandler, "Cotação atualizada com sucesso!", "success");
                } else {
                    // Criação (POST)
                    await FireServices.FireCreateQuotation(quotePayload);
                    showToastHelper(toastHandler, "Cotação salva com sucesso!", "success");
                }

                // Redirecionar para a listagem
                navigate("/incendio");

            } catch (error) {
                console.error("❌ Erro ao salvar cotação:", error);
                showToastHelper(toastHandler, "Erro ao salvar cotação.", "error");
            } finally {
                setIsSaving(false);
            }
        },
        [formRef, toastHandler, navigate]
    );

    return {
        handleSaveQuotation,
        isSaving,
    };
}
